import { useLayoutEffect, useState } from "react";

interface WindowSize<T extends number | undefined = number | undefined> {
  width: T;
  height: T;
}

interface UseWindowSizeOptions<InitializeWithValue extends boolean | undefined> {
  initializeWithValue: InitializeWithValue;
  debounceDelay?: number;
}

const IS_SERVER = typeof window === "undefined";

export function useWindowSize(
  options: Partial<UseWindowSizeOptions<boolean>> = {},
): WindowSize | WindowSize<number> {
  let { initializeWithValue = true } = options;
  if (IS_SERVER) {
    initializeWithValue = false;
  }

  const [windowSize, setWindowSize] = useState<WindowSize>(() => {
    if (initializeWithValue) {
      return {
        width: window.innerWidth,
        height: window.innerHeight,
      };
    }
    return {
      width: undefined,
      height: undefined,
    };
  });

  const debouncedSetWindowSize = useDebounceCallback(setWindowSize, options?.debounceDelay);

  function handleSize() {
    const setSize = options.debounceDelay ? debouncedSetWindowSize : setWindowSize;

    setSize({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }

  useEventListener("resize", handleSize);

  // Set size at the first client-side load
  useIsomorphicLayoutEffect(() => {
    handleSize();
  }, []);

  return windowSize;
}

import { useCallback, useEffect, useRef } from "react";

// eslint-disable-next-line
const useDebounceCallback = <T extends (...args: any[]) => any>(callback: T, delay?: number): T => {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef<NodeJS.Timeout>(null);

  // Update the callback to be used, if it changes.
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  return useCallback(
    // eslint-disable-next-line
    (...args: any[]) => {
      // Clear the timeout if the callback is invoked again within the delay period.
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Invoke the callback after the delay period.
      // @ts-ignore
      timeoutRef.current = setTimeout(() => callbackRef.current(...args), delay);
    },
    [delay],
  ) as T;
};

const useEventListener = (eventName: string, handler: EventListener, element = window) => {
  useEffect(() => {
    // Add event listener
    element.addEventListener(eventName, handler);

    // Remove event listener on cleanup
    return () => {
      element.removeEventListener(eventName, handler);
    };
  }, [eventName, handler, element]); // Re-run if eventName or handler changes
};

const useIsomorphicLayoutEffect = IS_SERVER ? useEffect : useLayoutEffect;
